import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useManualTimer } from '@core/myta/hooks/timer';
import { useMytaverseEvent } from '../../../providers';

import { mytaverseWebsocketLogger } from '@providers/MytaverseProvider';

import { UseHandleUEConnectionType } from './interfaces';
import { StreamingProviders } from '../../../providers/MytaverseEventProvider/interfaces';

export const UE5_WS_SETTLE_MS = 3000;

export const useHandleUEConnection: UseHandleUEConnectionType = () => {
  const {
    isStreamLoaded,
    isTeleporting,
    isTeleportingToRoomByUnreal,
    setIsTeleportingToRoomByUnreal,
    setUe5WebsocketConnected,
    markUeWorldUp,
    streamingProvider,
    setUe5CoreWeaveDisabled,
  } = useMytaverseEvent();

  const { timer, intervalId, startTimer, clearTimer } = useManualTimer();
  const { t: translate } = useTranslation();

  const isTeleportRun = isTeleporting || isTeleportingToRoomByUnreal;

  useEffect(() => {
    if (timer < 1 && !!intervalId) {
      clearTimer();
      mytaverseWebsocketLogger.log(
        `UE5 didn't reconnect to WS during 35 seconds`,
      );
    }
  }, [clearTimer, intervalId, timer, translate]);

  useEffect(() => {
    if (isStreamLoaded === false && intervalId) {
      clearTimer();
      mytaverseWebsocketLogger.log(`Stop UE5 WS connection monitor timer`);
    }
  }, [isStreamLoaded, clearTimer, intervalId]);

  const handleUe5WsConnected = useCallback(() => {
    mytaverseWebsocketLogger.log('UE5 connected');

    setTimeout(() => {
      setUe5WebsocketConnected(true);
      markUeWorldUp();
    }, UE5_WS_SETTLE_MS);

    if (streamingProvider === StreamingProviders.CoreWeave) {
      setTimeout(() => {
        setUe5CoreWeaveDisabled(false);
      }, UE5_WS_SETTLE_MS);
    }

    if (isTeleportRun) {
      if (isTeleportingToRoomByUnreal) {
        mytaverseWebsocketLogger.log(
          'Close teleport modal due to UE5 connected',
        );
        setIsTeleportingToRoomByUnreal(false);
      }
    }

    if (intervalId) {
      clearTimer();
    }
  }, [
    isTeleportingToRoomByUnreal,
    setIsTeleportingToRoomByUnreal,
    isTeleportRun,
    intervalId,
    streamingProvider,
    markUeWorldUp,
  ]);

  const handleUe5WsDisconnected = useCallback(() => {
    if (!isTeleportRun) {
      mytaverseWebsocketLogger.log('UE5 disconnected');
    } else {
      mytaverseWebsocketLogger.log('UE5 disconnected due teleport to room');
    }

    setUe5WebsocketConnected(false);

    //for CoreWeave there is no need to display notification and reload page
    if (streamingProvider === StreamingProviders.CoreWeave) {
      setUe5CoreWeaveDisabled(true);
      return;
    }

    if (isTeleportRun) {
      return;
    }

    startTimer();
  }, [isTeleportRun, streamingProvider]);

  return {
    handleUe5WsConnected,
    handleUe5WsDisconnected,
  };
};
